/**
 * The taxonomy rail an administrator edits, and the reads the administration panel performs (TASK-0052).
 *
 * The two levels are separate hooks on purpose: a subcategory list is meaningless without its parent, so its key carries
 * the parent identifier and its hook refuses to run without one. Every mutation invalidates the *prefix*
 * `['admin', 'categories']` rather than one entry, because a change to a category also changes the child count on the
 * list row and the detail payload the rows were read from — patching the cache by hand would be a second model of a rule
 * the server already applies.
 *
 * The reads take `includeDeleted` from the panel, because an administrator is the one persona that has to see a removed
 * row in order to restore it.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  adminCategoryApi,
  adminSubCategoryApi,
  type CategoryCreatePayload,
  type CategoryUpdatePayload,
  type SubCategoryCreatePayload,
  type SubCategoryUpdatePayload,
} from '@/features/admin/api/adminCategoryApi';
import { queryKeys } from '@/lib/queryKeys';

/** Every cache entry the taxonomy owns; the prefix a mutation invalidates. */
const TAXONOMY_PREFIX = ['admin', 'categories'] as const;

/**
 * The picker's own entries, invalidated by the same mutations.
 *
 * The rail a user chooses from is a *projection* of the rail an administrator edits (live, enabled rows only), so a
 * create, rename, disable or delete changes both. Invalidating only the administrative prefix would leave a prompt's
 * filing picker offering a row that was just retired — the exact write the server would then refuse.
 */
const PICKER_TAXONOMY_PREFIX = ['taxonomy'] as const;

/**
 * Reads the category rail.
 *
 * @param includeDeleted - Whether soft-deleted categories are included; the panel passes `true` so it can restore one.
 * @param enabled - Whether to run the query at all; only an administrator may read it.
 * @returns Query result carrying the categories in presentation order, with their live child counts.
 */
export function useAdminCategories(includeDeleted = true, enabled = true) {
  return useQuery({
    queryKey: queryKeys.adminCategories(includeDeleted),
    enabled,
    queryFn: () => adminCategoryApi.list({ includeDeleted }),
  });
}

/**
 * Reads the subcategories of one parent.
 *
 * @param categoryId - Parent category identifier; the query is skipped while it is empty.
 * @param includeDeleted - Whether soft-deleted subcategories are included.
 * @param enabled - Whether to run the query at all.
 * @returns Query result carrying the subcategories in presentation order.
 */
export function useAdminSubCategories(
  categoryId: string | null,
  includeDeleted = true,
  enabled = true,
) {
  const parent = categoryId ?? '';

  return useQuery({
    queryKey: queryKeys.adminSubCategories(parent, includeDeleted),
    enabled: enabled && parent.length > 0,
    queryFn: () => adminSubCategoryApi.list(parent, { includeDeleted }),
  });
}

/** Creates a category and refreshes everything the taxonomy reads. */
export function useCreateCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CategoryCreatePayload) => adminCategoryApi.create(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TAXONOMY_PREFIX });
      void queryClient.invalidateQueries({ queryKey: PICKER_TAXONOMY_PREFIX });
    },
  });
}

/** Renames or reorders a category. */
export function useUpdateCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ categoryId, payload }: { categoryId: string; payload: CategoryUpdatePayload }) =>
      adminCategoryApi.update(categoryId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TAXONOMY_PREFIX });
      void queryClient.invalidateQueries({ queryKey: PICKER_TAXONOMY_PREFIX });
    },
  });
}

/** Enables or disables a category. */
export function useSetCategoryState() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ categoryId, isActive }: { categoryId: string; isActive: boolean }) =>
      adminCategoryApi.setState(categoryId, isActive),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TAXONOMY_PREFIX });
      void queryClient.invalidateQueries({ queryKey: PICKER_TAXONOMY_PREFIX });
    },
  });
}

/** Soft-deletes a category; a `409` means it still holds a live subcategory. */
export function useDeleteCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (categoryId: string) => adminCategoryApi.remove(categoryId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TAXONOMY_PREFIX });
      void queryClient.invalidateQueries({ queryKey: PICKER_TAXONOMY_PREFIX });
    },
  });
}

/** Brings a soft-deleted category back, disabled. */
export function useRestoreCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (categoryId: string) => adminCategoryApi.restore(categoryId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TAXONOMY_PREFIX });
      void queryClient.invalidateQueries({ queryKey: PICKER_TAXONOMY_PREFIX });
    },
  });
}

/** Creates a subcategory beneath its parent. */
export function useCreateSubCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      categoryId,
      payload,
    }: {
      categoryId: string;
      payload: SubCategoryCreatePayload;
    }) => adminSubCategoryApi.create(categoryId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TAXONOMY_PREFIX });
      void queryClient.invalidateQueries({ queryKey: PICKER_TAXONOMY_PREFIX });
    },
  });
}

/** Renames or reorders a subcategory. */
export function useUpdateSubCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      categoryId,
      subCategoryId,
      payload,
    }: {
      categoryId: string;
      subCategoryId: string;
      payload: SubCategoryUpdatePayload;
    }) => adminSubCategoryApi.update(categoryId, subCategoryId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TAXONOMY_PREFIX });
      void queryClient.invalidateQueries({ queryKey: PICKER_TAXONOMY_PREFIX });
    },
  });
}

/** Enables or disables a subcategory. */
export function useSetSubCategoryState() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      categoryId,
      subCategoryId,
      isActive,
    }: {
      categoryId: string;
      subCategoryId: string;
      isActive: boolean;
    }) => adminSubCategoryApi.setState(categoryId, subCategoryId, isActive),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TAXONOMY_PREFIX });
      void queryClient.invalidateQueries({ queryKey: PICKER_TAXONOMY_PREFIX });
    },
  });
}

/** Soft-deletes a subcategory; a later create of the same slug asks for a restore first. */
export function useDeleteSubCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ categoryId, subCategoryId }: { categoryId: string; subCategoryId: string }) =>
      adminSubCategoryApi.remove(categoryId, subCategoryId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TAXONOMY_PREFIX });
      void queryClient.invalidateQueries({ queryKey: PICKER_TAXONOMY_PREFIX });
    },
  });
}

/** Brings a soft-deleted subcategory back, disabled. */
export function useRestoreSubCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ categoryId, subCategoryId }: { categoryId: string; subCategoryId: string }) =>
      adminSubCategoryApi.restore(categoryId, subCategoryId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TAXONOMY_PREFIX });
      void queryClient.invalidateQueries({ queryKey: PICKER_TAXONOMY_PREFIX });
    },
  });
}
